<script setup lang="ts">
import { ref, onBeforeMount, watch, computed } from "vue";
import { useApiClient } from "@/api/client";
import dayjs from "dayjs";
import InvokedActionDO = Modules.BookingExperts.Data.InvokedActionDO;
import PieChart from "@/modules/pro_loyalty/components/PieChart.vue";
import PieSegmentsDTO = Modules.BookingExperts.Data.PieSegmentsDTO;
import MostUsedActionDTO = Modules.BookingExperts.Data.MostUsedActionDTO;
import {
	humanizePast,
	mostUsedOptions,
	tabOptions,
	mostUsedTableHeaders,
} from "./LoyaltyUtils";

const currentPage = ref<number>(1);
const itemsPerPage = ref<number>(10);
const totalItems = ref<number>(1);

const isLoading = ref(true);

const invokedActions = ref<InvokedActionDO[]>([]);

const getInvokedActions = async () => {
	const response = await useApiClient("proLoyalty").getInvokedActions({
		page: currentPage.value,
		per_page: itemsPerPage.value,
		sortBy: [],
	});
	if (response.isOk()) {
		invokedActions.value = response.value.data;
		totalItems.value = response.value.total;
	}
};

const noOfPages = computed(() =>
	Math.ceil(totalItems.value / itemsPerPage.value)
);

const sparklineTaskValues = ref<SparklineValuesDTO | undefined>();
const sparklineLabelValues = ref<SparklineValuesDTO | undefined>();

const getSparklineValues = async () => {
	isLoading.value = true;
	const [labelResponse, taskResponse] = await Promise.all([
		useApiClient("proLoyalty").getSparklineValues("label"),
		useApiClient("proLoyalty").getSparklineValues("task"),
	]);

	if (labelResponse.isOk()) {
		sparklineLabelValues.value = labelResponse.value;
	}

	if (taskResponse.isOk()) {
		sparklineTaskValues.value = taskResponse.value;
	}
	isLoading.value = false;
};

const isTask = (type: string): boolean => {
	return type.endsWith("BexTodo");
};

watch(currentPage, () => getInvokedActions());

const mostUsedObjects = ref<MostUsedActionDTO[]>([]);
const mostUsedTabSelection = ref("task");

const getMostUsed = async () => {
	const response = await useApiClient("proLoyalty").getMostUsed(
		mostUsedTabSelection.value
	);
	if (response.isOk()) {
		mostUsedObjects.value = response.value;
	}
};

watch(mostUsedTabSelection, () => getMostUsed());

const newReturningTabSelection = ref("24m");
const customerTypes = ref<PieSegmentsDTO[]>([]);

const customerTypeDistribution = async () => {
	const response = await useApiClient("proLoyalty").getCustomerType(
		newReturningTabSelection.value
	);
	if (response.isOk()) {
		customerTypes.value = response.value;
	}
};

watch(newReturningTabSelection, () => customerTypeDistribution());

const frequencyTabSelection = ref("24m");
const customerFrequencies = ref<PieSegmentsDTO[]>([]);

const customerFrequencyDistribution = async () => {
	const response = await useApiClient("proLoyalty").getCustomerFrequency(
		frequencyTabSelection.value
	);
	if (response.isOk()) {
		customerFrequencies.value = response.value;
	}
};

watch(frequencyTabSelection, () => customerFrequencyDistribution());

onBeforeMount(() => {
	getSparklineValues();
	getInvokedActions();
	getMostUsed();
	customerTypeDistribution();
	customerFrequencyDistribution();
});
</script>

<template>
	<div class="mt-10" v-if="!isLoading">
		<v-row>
			<v-col cols="6">
				<v-row>
					<v-col cols="12">
						<v-card class="">
							<v-tabs
								class="mt-1 px-1 bgc-white"
								density="compact"
								v-model="newReturningTabSelection"
							>
								<v-card-title> New/returning customers: </v-card-title>
								<v-spacer />
								<v-tab v-for="tabOption in tabOptions" :value="tabOption.value">
									<div class="text-caption">
										{{ $t(tabOption.label) }}
									</div>
								</v-tab>
							</v-tabs>
							<pie-chart :items="customerTypes" />
						</v-card>
					</v-col>
					<v-col cols="12">
						<v-card class="">
							<v-tabs
								class="mt-1 px-1 bgc-white"
								density="compact"
								v-model="frequencyTabSelection"
							>
								<v-card-title> Customer frequency: </v-card-title>
								<v-spacer />
								<v-tab v-for="tabOption in tabOptions" :value="tabOption.value">
									<div class="text-caption">
										{{ $t(tabOption.label) }}
									</div>
								</v-tab>
							</v-tabs>
							<pie-chart :items="customerFrequencies" />
						</v-card>
					</v-col>
					<v-col cols="12">
						<v-card class="">
							<v-tabs
								class="mt-1 px-1 bgc-white"
								density="compact"
								v-model="mostUsedTabSelection"
							>
								<v-card-title> Most used (top 5): </v-card-title>
								<v-spacer />
								<v-tab
									v-for="tabOption in mostUsedOptions"
									:value="tabOption.value"
								>
									<div class="text-caption">
										{{ $t(tabOption.label) }}
									</div>
								</v-tab>
							</v-tabs>
							<div>
								<v-data-table
									:headers="mostUsedTableHeaders"
									:items="mostUsedObjects"
								>
									<template v-slot:bottom></template>
								</v-data-table>
							</div>
						</v-card>
					</v-col>
				</v-row>
			</v-col>
			<v-col cols="6">
				<v-row>
					<v-col cols="12">
						<v-row>
							<v-col cols="6">
								<v-card class="ma-0">
									<div class="pa-5">
										<div class="d-flex align-center justify-space-between">
											<v-card-title>Tasks:</v-card-title>
											<v-avatar color="#546E7A" class="rounded-md text-white">
												<v-icon size="20">fa: fa-light fa-list-check</v-icon>
											</v-avatar>
										</div>
										<div class="d-flex">
											<v-card-title style="font-size: 24px">
												{{ sparklineTaskValues.sum }}
											</v-card-title>
											<v-card-subtitle class="mt-4 pa-0"
												>Created in Bex</v-card-subtitle
											>
										</div>
									</div>
									<div>
										<v-sparkline
											:model-value="sparklineTaskValues.values"
											:gradient="[
												'rgba(84, 110, 122, 0.1)',
												'rgba(84, 110, 122, 0.1)',
											]"
											color="#546E7A"
											height="85"
											stroke-linecap="round"
											line-width="2"
											fill
											smooth
											type="trend"
											:show-labels="false"
											padding="0"
										>
										</v-sparkline>
									</div>
								</v-card>
							</v-col>
							<v-col cols="6">
								<v-card class="ma-0">
									<div class="pa-5">
										<div class="d-flex align-center justify-space-between">
											<v-card-title>Labels:</v-card-title>
											<v-avatar color="#FBC02D" class="rounded-md">
												<v-icon size="20">fa: fa-light fa-tags</v-icon>
											</v-avatar>
										</div>
										<div class="d-flex">
											<v-card-title style="font-size: 24px">
												{{ sparklineLabelValues.sum }}
											</v-card-title>
											<v-card-subtitle class="mt-4 pa-0"
												>Created in Bex</v-card-subtitle
											>
										</div>
									</div>
									<div>
										<v-sparkline
											:model-value="sparklineLabelValues.values"
											:gradient="[
												'rgba(251, 192, 45, 0.1)',
												'rgba(251, 192, 45, 0.1)',
											]"
											color="#FBC02D"
											height="85"
											stroke-linecap="round"
											line-width="2"
											fill
											smooth
											type="trend"
											:show-labels="false"
											padding="0"
										>
										</v-sparkline>
									</div>
								</v-card>
							</v-col>
						</v-row>
					</v-col>
					<v-col cols="12">
						<v-card class="px-5">
							<v-card-title class="px-0 mt-1"> Automation: </v-card-title>
							<v-timeline
								v-if="invokedActions.length > 0"
								class="my-5"
								align="start"
								side="end"
							>
								<v-timeline-item
									v-for="invokedAction in invokedActions"
									:dot-color="
										isTask(invokedAction.actionable_type)
											? `#546E7A`
											: `#FBC02D`
									"
									:icon="
										isTask(invokedAction.actionable_type)
											? `fa: fa-light fa-list-check`
											: `fa: fa-light fa-tags`
									"
								>
									<div class="d-flex tl-container text-caption">
										<div style="width: 100px">
											<strong
												class="me-4 mt-2 text-caption"
												v-tooltip="
													dayjs(invokedAction.created_at).format(
														'dddd D MMMM HH:mm'
													)
												"
												>{{ humanizePast(invokedAction.created_at) }}</strong
											>
										</div>
										<div>
											Added
											<strong v-if="isTask(invokedAction.actionable_type)"
												>task</strong
											><strong v-else>label</strong> to reservation #{{
												invokedAction.reservation_id
											}}
											<div
												class="text-caption"
												v-if="isTask(invokedAction.actionable_type)"
											>
												"<strong
													>{{ invokedAction.actionable?.quantity ?? 0 }} x
													{{ invokedAction.actionable?.name ?? "" }}</strong
												>"
											</div>
											<div v-else class="text-caption">
												<v-chip
													:color="
														invokedAction.actionable?.background ?? `#cccccc`
													"
													class="mt-2"
													label
												>
													<div class="text-caption">
														{{ invokedAction.actionable?.name ?? "" }}
													</div>
												</v-chip>
											</div>
										</div>
									</div>
								</v-timeline-item>
							</v-timeline>

							<v-empty-state v-else size="200" text-width="250" class="mx-10">
								<template v-slot:title>
									<div class="text-h6 text-high-emphasis">
										No automated actions triggered
									</div>
								</template>

								<template v-slot:text>
									<div class="text-body-1">
										Go to the scenario's tab to configure automated actions.
									</div>
								</template>
							</v-empty-state>

							<v-pagination
								v-model="currentPage"
								density="compact"
								:length="noOfPages"
								class="ma-0 mb-2 mx-10"
							></v-pagination>
						</v-card>
					</v-col>
				</v-row>
			</v-col>
		</v-row>
	</div>
</template>

<style scoped lang="scss">
.bgc-white {
	background-color: var(--color-background) !important;
}
.tl-container {
	height: 60px;
	overflow: hidden;
}
.bold {
	font-weight: bold;
}
</style>
